<template>
  <ion-page>
    <!--inner header start-->
    <ion-header class="header-container">
      <ion-toolbar class="header-toolbar">
        <ion-title class="page-title">My Cart</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content v-if="!cartOrders.length">
      <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
        <ion-refresher-content
          :pulling-icon="chevronDownCircleOutline"
          pulling-text="Pull to refresh"
          refreshing-spinner="circles"
          refreshing-text="Refreshing..."
        >
        </ion-refresher-content>
      </ion-refresher>
      <div style="text-align: center; margin-top: 1em">Your cart is empty.</div>
    </ion-content>
    <ion-content v-else>
      <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
        <ion-refresher-content
          :pulling-icon="chevronDownCircleOutline"
          pulling-text="Pull to refresh"
          refreshing-spinner="circles"
          refreshing-text="Refreshing..."
        >
        </ion-refresher-content>
      </ion-refresher>
      <div v-for="cart in cartOrders" :key="cart.id">
        <ion-row class="cartlist">
          <a
            :href="'/checkout?cafeId=' + cart.cafe.cafeId"
            style="text-decoration: none; display: contents"
          >
            <ion-col size="4">
              <img :src="cart.cafe.shopImgUrl" class="order-icon" />
            </ion-col>
            <ion-col class="cart-col2" size="6">
              <span class="cart-txtbold">{{ cart.cafe.shopName }}</span>
              <div>
                <span class="cart-txtlight">{{ cart.quantity }} item/s</span>
                <template v-if="cart.cafe.distance">
                  <ion-icon
                    src="assets/icon/dot-separate.svg"
                    class="dotsep-icon"
                  />
                  <ion-icon
                    src="assets/icon/distance.svg"
                    class="banner-icon"
                  ></ion-icon>
                  <span class="cart-txtlight">{{ cart.cafe.distance }}</span>
                </template>
              </div>
              <div class="cart-ready">
                <ion-icon
                  src="assets/icon/time-outline.svg"
                  class="banner-icon"
                ></ion-icon>
                <span class="cart-txtlight">Ready in 5-10 min</span>
              </div>
            </ion-col>
          </a>
          <ion-col class="cart-col2">
            <a @click="presentAlert(cart.cafe.cafeId)">
              <img
                src="assets/icon/trash.png"
                class="trash-icon"
                color="primary"
              />
            </a>
          </ion-col>
        </ion-row>
      </div>
    </ion-content>
    <TheFooter name="TheFooter" />
  </ion-page>
</template>
<script>
import { defineComponent } from "vue";
import axios from "axios";
import TheFooter from "@/components/TheFooter.vue";
import {
  IonContent,
  IonHeader,
  IonButtons,
  IonIcon,
  IonToolbar,
  IonPage,
  IonRow,
  IonCol,
  IonAvatar,
  IonTitle,
  IonRefresher,
  IonRefresherContent,
  alertController,
  toastController,
} from "@ionic/vue";
import { chevronDownCircleOutline } from "ionicons/icons";
import { distanceCache } from "@/utils/distanceCache";

const BASE_URI =
  process.env.VUE_APP_BASE_URI !== undefined
    ? process.env.VUE_APP_BASE_URI
    : "https://api.onecoffeeapp.com.au";

export default defineComponent({
  name: "CartPage",
  components: {
    IonContent,
    IonHeader,
    IonButtons,
    IonIcon,
    IonToolbar,
    IonPage,
    IonRow,
    IonCol,
    IonAvatar,
    IonTitle,
    IonRefresher,
    IonRefresherContent,
    TheFooter,
  },
  data() {
    return {
      cartOrders: [],
      totalCartQty: 0,
      subTotalCartPrice: 0,
      serviceFee: 0,
      totalCartPrice: 0,
      promptMessage: "",
      chevronDownCircleOutline,
    };
  },
  methods: {
    async handleRefresh(event) {
      const userId = localStorage.getItem("id") || null;
      await this.getCartOrders(userId);
      // Complete the refresh animation
      event.target.complete();
    },
    async presentAlert(id) {
      if (id) {
        const alert = await alertController.create({
          subHeader: "Are you sure you want to delete this restaurant?",
          buttons: [
            {
              text: "No",
              role: "cancel",
              handler: () => {},
            },
            {
              text: "Yes",
              role: "confirm",
              handler: () => {
                this.removeCartOrder(id);
              },
            },
          ],
        });

        await alert.present();
        await alert.onDidDismiss();
      } else {
        const alert = await alertController.create({
          message: this.promptMessage,
          buttons: ["OK"],
        });

        await alert.present();
        await alert.onDidDismiss();
        window.location.reload();
      }
    },
    async getCartOrders(userId) {
      let axiosConfig = {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json;charset=UTF-8",
        },
      };

      let url = userId
        ? BASE_URI +
          "/cart/get-cart-orders/?userId=" +
          userId +
          "&status=pending" +
          "&groupByCafeId=true"
        : BASE_URI + "/cafe/get-coffee-shops";

      let result = await axios
        .get(url, axiosConfig)
        .then((res) => res.data)
        .catch((err) => {
          throw err.response.status;
        });

      this.cartOrders = result && result.data.length ? await result.data : [];

      for (let i = 0; i < this.cartOrders.length; i++) {
        this.totalCartQty += this.cartOrders[i].quantity;
        this.subTotalCartPrice += this.cartOrders[i].totalPrice;
      }

      this.serviceFee = this.totalCartQty * 0.5;
      this.totalCartPrice = this.subTotalCartPrice + this.serviceFee;

      // Get user's location and calculate distances
      this.getUserLocation();
    },
    async removeCartOrder(cafeId) {
      let headers = {
        Authorization: localStorage.getItem("token"),
        "Content-Type": "application/json;charset=UTF-8",
      };

      let paramsData = {
        userId: localStorage.getItem("id"),
        cafeId: cafeId,
      };

      await axios
        .delete(BASE_URI + "/cart/delete-cart-order", {
          headers,
          data: paramsData,
        })
        .then((res) => {
          this.presentToast(
            res.data.message || "Item has been removed from the cart."
          );
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        })
        .catch((err) => {
          throw err.response.status;
        });
    },
    getUserLocation() {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // eslint-disable-next-line no-undef
          const userLocation = new google.maps.LatLng(
            position.coords.latitude,
            position.coords.longitude
          );

          // Calculate distances for each cafe in cart
          this.cartOrders.forEach((cart) => {
            this.calculateDistance(userLocation, cart.cafe);
          });
        },
        (error) => {
          console.error("Error getting user's location: " + error.message);
        }
      );
    },
    calculateDistance(userLocation, cafe) {
      // Use cafe UUID or ID as cache key
      const cafeId = cafe.cafeId || cafe.uuid || cafe.id;

      // Check cache first
      const cached = distanceCache.get(cafeId, userLocation);
      if (cached) {
        cafe.distance = cached.distance;
        cafe.duration = cached.duration;
        return;
      }

      // Cache miss - calculate via API
      const destination = cafe.address;

      // eslint-disable-next-line no-undef
      const service = new google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [userLocation],
          destinations: [destination],
          travelMode: "DRIVING",
          // eslint-disable-next-line no-undef
          unitSystem: google.maps.UnitSystem.METRIC,
          avoidHighways: false,
          avoidTolls: false,
        },
        (response, status) => {
          if (status === "OK") {
            const distance = response.rows[0].elements[0].distance.text;
            const duration = response.rows[0].elements[0].duration.text;

            // Update cafe object with distance and force reactivity
            cafe.distance = distance;
            cafe.duration = duration;

            // Store in cache
            distanceCache.set(cafeId, userLocation, distance, duration);

            // Force Vue to update the view
            this.$forceUpdate();
          } else {
            console.error("Error calculating distance:", status);
            cafe.distance = null;
          }
        }
      );
    },
    async presentToast(message, type = "success") {
      const cssClass =
        type === "success" ? "custom-toast" : `custom-toast toast-${type}`;

      const toast = await toastController.create({
        message: message,
        duration: 2000,
        position: "top",
        cssClass: cssClass,
      });

      await toast.present();
    },
  },
  async created() {
    const userId = localStorage.getItem("id") || null;
    await this.getCartOrders(userId);
  },
});
</script>
<style scoped>
/* Header Container */
.header-container {
  --background: #f87e63;
}

.header-toolbar {
  --background: #f87e63;
  --border-width: 0;
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 64px;
}

/* Page Title */
.page-title {
  --color: #ffffff;
  font-family: "Gilroy", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
}

/* Content Styles */
.cartlist {
  border-bottom: 1px solid #f3f3f3;
  padding-top: 15px;
  padding-bottom: 10px;
}
.order-icon {
  width: 80px;
  height: 80px;
  margin-left: 20px;
  margin-right: 10px;
  object-fit: cover;
  border-radius: 8px;
}
.cart-col2 {
  margin-left: 10px;
}
.cart-txtlight {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 135%;
  color: #828282;
}
.cart-txtbold {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  color: #1b1b1b;
  display: block;
  padding-top: 5px;
}
.dotsep-icon {
  vertical-align: middle;
  width: 3px;
  height: 3px;
  padding-right: 5px;
  padding-left: 5px;
}
.trash-icon {
  width: 16px;
  margin-top: 15px;
}
.banner-icon {
  vertical-align: middle;
  padding-right: 5px;
  width: 11px;
}
.cart-ready {
  display: block;
  margin-top: 2px;
}
</style>
